import { useState } from "react";
import {
  Button,
  CircularProgress,
  Paper,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useRefresh } from "react-admin";
import { useAppSelector } from "../../store/hooks";
import GroupPermissionFunction from "../../utils/groupPermissionFunction";
import { UserGroupsEnum } from "../../enums/UserGroupsEnum";
import { CookService } from "../../services/CookServices";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";

// Common Styles
import {
  CustomTableRowTR,
  SectionHeader,
  SectionTitle,
  StatusBadge,
  StyledTable,
  TableData,
  TableHeader,
  TableTopHeader,
  TableWrapper,
  Title,
  WorkspacePanel,
  WorkspaceSection,
  WorkSpaceTitleRow,
} from "../../styles/commonStyles";

// Icons & Colors
import { ImagePlaceholderIcon } from "../Svg";
import { colors } from "../../styles/colors";

const cookService = new CookService();

type RatingTags = Record<string, string[]>;

interface CustomerRating {
  id?: string | number;
  overallRating?: number | string | null;
  ratingTags?: RatingTags;
  comments?: string | null;
  photos?: string[];
}

interface Props {
  record?: {
    customerRatings?: CustomerRating[];
  };
}

const MAX_VISIBLE_IMAGES = 6;


const normalizeRating = (value: unknown): number => {
  const num = Number(value);
  return Number.isFinite(num) && num >= 0 ? num : 0;
};

const getRatingBackground = (rating: number) => {
  if (rating >= 4.5) return "linear-gradient(135deg,#10b981,#059669)";
  if (rating >= 3.5) return "linear-gradient(135deg,#3b82f6,#2563eb)";
  if (rating >= 2.5) return "linear-gradient(135deg,#f59e0b,#d97706)";
  return "linear-gradient(135deg,#ef4444,#dc2626)";
};

const getRatingBorderColor = (rating: number) => {
  if (rating >= 4.5) return "#059669";
  if (rating >= 3.5) return "#2563eb";
  if (rating >= 2.5) return "#d97706";
  return "#dc2626";
};

const EmptyState = ({ text }: { text: string }) => (
  <div
    style={{
      width: "100%",
      padding: "24px",
      borderRadius: "8px",
      backgroundColor: "rgba(232,235,237,0.3)",
      color: "#64748b",
      fontSize: "14px",
      textAlign: "center",
    }}
  >
    {text}
  </div>
);

const RatingBadge = ({ rating }: { rating: number }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "60px",
      height: "36px",
      borderRadius: "20px",
      background: getRatingBackground(rating),
      color: "#fff",
      fontWeight: 600,
      fontSize: "14px",
      border: `1px solid ${getRatingBorderColor(rating)}`,
    }}
  >
    {rating.toFixed(1)} ⭐
  </div>
);

const BadgeList = ({ items }: { items?: string[] }) => {
  if (!Array.isArray(items) || items.length === 0) {
    return <span>-</span>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      {items.map((item) => (
        <StatusBadge key={item} variant="veg">
          {item}
        </StatusBadge>
      ))}
    </div>
  );
};

const ChefForPartyCustomerRating = ({ record }: Props) => {
  const refresh = useRefresh();
  const userDetails = useAppSelector((state) => state.userDetails.value);
  const groupsArray: string[] = userDetails.adminUser.accessGroups;
  const isAdmin = GroupPermissionFunction(groupsArray, [UserGroupsEnum.ADMIN]);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const ratings = record?.customerRatings ?? [];
  const hasRatings = ratings.length > 0;

  const allImages = ratings.flatMap((r) => r.photos ?? []);
  const visibleImages = allImages.slice(0, MAX_VISIBLE_IMAGES);
  const remainingImages = allImages.length - visibleImages.length;

  const handleDelete = (ratingId: number) => {
    EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
      message: "Are you sure you want to delete this customer rating?",
      onClose: (confirmed: boolean) => {
        if (!confirmed) return;
        setDeletingId(ratingId);
        cookService.deleteCookRating(ratingId)
          .then(() => {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
              message: "Rating deleted successfully",
              type: CustomSnackbarTypes.SUCCESS,
            });
            refresh();
          })
          .catch(() => {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
              message: "Failed to delete rating",
              type: CustomSnackbarTypes.ERROR,
            });
          })
          .finally(() => setDeletingId(null));
      },
      title: "Confirmation",
    });
  };

  return (
    <>
      <TableWrapper>
        <TableTopHeader>
          <Title>Customer Ratings</Title>
        </TableTopHeader>

        <TableContainer component={Paper} sx={{ borderRadius: "8px" }}>
          <StyledTable>
            <TableHead>
              <TableRow sx={{
                backgroundColor: "rgba(232, 235, 237, 0.3) !important",
              }}>
                <TableHeader>Overall</TableHeader>
                <TableHeader>Behaviour</TableHeader>
                <TableHeader>Food</TableHeader>
                <TableHeader>Hygiene</TableHeader>
                <TableHeader style={{ width: "320px", whiteSpace: "normal" }}>
                  Comments
                </TableHeader>
                {isAdmin && <TableHeader>Action</TableHeader>}
              </TableRow>
            </TableHead>

            <TableBody>
              {!hasRatings ? (
                <TableRow >
                  <TableData colSpan={isAdmin ? 6 : 5}>
                    <EmptyState text="No customer ratings available" />
                  </TableData>
                </TableRow>
              ) : (
                ratings.map((row, index) => {
                  const rating = normalizeRating(row.overallRating);
                  const ratingId = Number(row.id);

                  return (
                    <CustomTableRowTR key={row.id ?? index}>
                      <TableData align="center">
                        <RatingBadge rating={rating} />
                      </TableData>

                      <TableData>
                        <BadgeList
                          items={row.ratingTags?.["Behavior related"]}
                        />
                      </TableData>

                      <TableData>
                        <BadgeList
                          items={row.ratingTags?.["Food related"]}
                        />
                      </TableData>

                      <TableData>
                        <BadgeList
                          items={row.ratingTags?.["Hygiene Related"]}
                        />
                      </TableData>

                      <TableData
                        style={{
                          width: "320px",
                          maxWidth: "320px",
                          whiteSpace: "normal",
                          verticalAlign: "top",
                        }}
                      >
                        <div style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
                          {row.comments?.trim() || "-"}
                        </div>
                      </TableData>

                      {isAdmin && (
                        <TableData align="center">
                          <Button
                            size="small"
                            variant="contained"
                            color="error"
                            disabled={!ratingId || deletingId === ratingId}
                            onClick={() => handleDelete(ratingId)}
                          >
                            {deletingId === ratingId ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              "Delete"
                            )}
                          </Button>
                        </TableData>
                      )}
                    </CustomTableRowTR>
                  );
                })
              )}
            </TableBody>
          </StyledTable>
        </TableContainer>
      </TableWrapper>

      <br />

      <WorkspacePanel>
        <WorkspaceSection>
          <SectionHeader>
            <WorkSpaceTitleRow>
              <ImagePlaceholderIcon color={colors.primary} size={16} />
              <SectionTitle>Customer Rating Images</SectionTitle>
            </WorkSpaceTitleRow>
          </SectionHeader>
        </WorkspaceSection>

        {allImages.length === 0 ? (
          <EmptyState text="No images available" />
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
              gap: "12px",
              marginTop: "12px",
            }}
          >
            {visibleImages.map((src, index) => (
              <div
                key={index}
                style={{
                  position: "relative",
                  borderRadius: "8px",
                  overflow: "hidden",
                  border: "1px solid rgba(0,0,0,0.06)",
                }}
              >
                <img
                  src={src}
                  alt="Customer feedback"
                  loading="lazy"
                  onError={(e) =>
                    ((e.target as HTMLImageElement).style.display = "none")
                  }
                  style={{
                    width: "100%",
                    height: "160px",
                    objectFit: "cover",
                    display: "block",
                  }}
                />

                {index === MAX_VISIBLE_IMAGES - 1 &&
                  remainingImages > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: "rgba(0,0,0,0.55)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: "20px",
                        fontWeight: 600,
                      }}
                    >
                      +{remainingImages}
                    </div>
                  )}
              </div>
            ))}
          </div>
        )}
      </WorkspacePanel>
    </>
  );
};

export default ChefForPartyCustomerRating;
